import type { ComponentType, SVGProps } from 'react'
import { MarqueeRow } from '@/shared/ui/marquee'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import {
  MiniAi,
  MiniCode,
  MiniCreativity,
  MiniDesign,
  MiniEducation,
  MiniImagery,
  MiniMotion,
  MiniPrompts,
  MiniVideo,
} from './mini-icons'

const T = defineT({
  ai: { ar: 'ذكاء اصطناعي', en: 'AI' },
  design: { ar: 'تصميم', en: 'Design' },
  code: { ar: 'برمجة', en: 'Code' },
  video: { ar: 'فيديو', en: 'Video' },
  imagery: { ar: 'صور', en: 'Imagery' },
  motion: { ar: 'موشن', en: 'Motion' },
  prompts: { ar: 'برومبتات', en: 'Prompts' },
  education: { ar: 'تعليم', en: 'Education' },
  creativity: { ar: 'إبداع', en: 'Creativity' },
})

const ITEMS: Array<{ key: keyof typeof T; Icon: ComponentType<SVGProps<SVGSVGElement>> }> = [
  { key: 'ai', Icon: MiniAi },
  { key: 'design', Icon: MiniDesign },
  { key: 'code', Icon: MiniCode },
  { key: 'video', Icon: MiniVideo },
  { key: 'imagery', Icon: MiniImagery },
  { key: 'motion', Icon: MiniMotion },
  { key: 'prompts', Icon: MiniPrompts },
  { key: 'education', Icon: MiniEducation },
  { key: 'creativity', Icon: MiniCreativity },
]

/**
 * PRO brand strip — hand-drawn mini SVG icons (no emoji, no generic set).
 * Glass pills with icon tile + label, hairline gradient edges, soft glow.
 * Hover = PRO animation: tile flips orange, icon pops + spins micro, pill
 * lifts with glow ring sweep. Pauses on hover, RTL-aware, reduced-motion safe.
 */
export function HeroMarquee() {
  const { t } = useTranslation(T)

  return (
    <div className="relative overflow-hidden border-y border-border/40 bg-background/60 py-4 backdrop-blur-md">
      {/* hairline gradient edges */}
      <div aria-hidden className="absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div aria-hidden className="absolute inset-x-12 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      {/* soft center glow */}
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-24 w-[60%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />
      <MarqueeRow durationSec={45}>
        {ITEMS.map(({ key, Icon }) => (
          <span
            key={key}
            className="pill-hover group inline-flex items-center gap-2.5 rounded-full border border-border/60 bg-card/70 py-1.5 pe-5 ps-1.5 backdrop-blur-md"
          >
            <span className="mini-icon grid h-8 w-8 place-items-center rounded-full bg-primary/15 text-primary">
              <Icon className="h-[18px] w-[18px]" aria-hidden />
            </span>
            <span className="font-display text-base font-semibold tracking-tight text-foreground/80 transition-colors duration-300 group-hover:text-foreground">
              {t(key)}
            </span>
          </span>
        ))}
      </MarqueeRow>
    </div>
  )
}
